import { toast } from '/plugins/toaster.client.js';
import { baseFilterFieldModel } from './filter-field-model';
import moment from 'moment-timezone';

const _changeExcludeOperator = (operator) => {
  if (operator === 'not in (not in)') {
    // changing the value of exclude operator in query param
    // so that '(' does not cause waf error on refresh
    return 'not_in';
  }
  if (operator === 'not_in') {
    // changing the value of exclude operator back to its original value
    // while sending filter value to backend from query string
    return 'not in (not in)';
  }
  return operator;
};

export const getQueryString = (filters) =>
  filters.reduce((string, filter, currentIndex) => {
    const stringStart = currentIndex === 0 ? string : `${string}&`;
    let operator = _changeExcludeOperator(filter.operator);
    if (
      ['date', 'datetime', 'datetime2'].includes(filter.type) &&
      (filter.operator === 'range' || filter.operator === 'null')
    ) {
      return `${stringStart}filters[${filter.name}]=range [${filter.range.startDate},${filter.range.finishedDate}]`;
    }
    if (filter.isNumericType) {
      return `${stringStart}filters[${filter.name}]=range [${filter.minValue},${filter.maxValue},${
        filter.includeNulls || 'No'
      }]`;
    }

    // Used unique quotes to identify different values.
    const values = filter.values.map((value) => `\u201C${value}\u201D`);
    return `${stringStart}filters[${filter.name}]=${operator} [${encodeURIComponent(values)}]${
      filter.seperator ? '$sep$' + encodeURIComponent(filter.seperator) : ''
    }`;
  }, '?');

export const filterToQueryString = (filters, acWidgetFilters) => {
  const acWidgetFilterKeys = acWidgetFilters instanceof Object ? Object.keys(acWidgetFilters) : [];
  let queryString = getQueryString(filters);
  if (queryString.length > 2048) {
    toast.warn('Max filter length reached, please reduce the number of filters');
  }
  if (acWidgetFilterKeys.length > 0) {
    let acQueryString;
    acWidgetFilterKeys.forEach((key) => {
      acQueryString = `${key}=${encodeURIComponent(acWidgetFilters[key])}`;
      queryString = queryString.length > 0 ? queryString.concat(`&${acQueryString}`) : acQueryString;
    });
  }
  window.history.replaceState(null, null, queryString);
};

export const queryStringToFilter = (queryParams, preloadFilters) => {
  let filterModel = {};
  let fieldName;
  let values = '';
  let findIndex;
  let seperator;
  let operator;
  let paramsToKeep = {};
  let acWidgetFilters;
  //remove filters with no key before parsing query
  for (const [key, value] of Object.entries(queryParams).filter((param) => param[0])) {
    let decodedValue = decodeURIComponent(value);
    // ac-filters are used to filter access-control cockpit widgets.
    if (key.startsWith('ac-filters')) {
      acWidgetFilters = { ...acWidgetFilters, [key]: decodedValue };
      continue;
    }
    fieldName = fetchValues(key);
    findIndex = decodedValue.indexOf('[');
    seperator = decodedValue.split('$sep$').length > 1 ? decodedValue.split('$sep$')[1] : '';
    const hasField = preloadFilters.find(
      (field) => [field.Field, field.field].includes(fieldName) && !!field.isPageFilter
    );
    const hasNoPageFilters = preloadFilters.every((filter) => !filter.isPageFilter);
    if (hasField || preloadFilters.length === 0 || hasNoPageFilters) {
      values = fetchValues(decodedValue.substring(findIndex, decodedValue.length).trim());
      // Remove first and last quote and then splited by unique quotes.
      values = values.includes('\u201C')
        ? values.substring(1, values.length - 1).split('\u201D,\u201C')
        : values.split(',');
      operator = _changeExcludeOperator(decodedValue.substring(0, findIndex).trim());
      filterModel[fieldName] = buildFilterModel(values, operator, seperator);
      paramsToKeep[key] = decodedValue;
    }
  }
  if (preloadFilters && preloadFilters.length > 0) {
    resetUrlFilters(preloadFilters, paramsToKeep);
  }

  if (acWidgetFilters) {
    filterModel['ac-filters'] = acWidgetFilters;
  }
  return filterModel;
};

/**
 * Method to reset the url query sting based on the selected filter fields for the dashboard
 * @param {Object[]} preloadFilters holds the selected fields for filter of dashboard
 * @param {Object} queryParams key value pair for the filters in the querystring in the URL
 */
export const resetUrlFilters = (preloadFilters, queryParams) => {
  const queryKeys = Object.keys(queryParams);
  const queryValues = Object.values(queryParams);
  const queryString = queryKeys.reduce((string, query, index) => {
    let checkPreloadFilter = preloadFilters.find(
      (filter) => query.includes(filter.field) && (filter.isPageFilter || filter.isMandatoryParam)
    );
    if (checkPreloadFilter) {
      return `${string}&${query}=${queryValues[index]}`;
    }
    return string;
  }, '?');
  window.history.replaceState(null, null, queryString);
};

export const fetchValues = (value) => {
  let result = '';
  value.replace(/\[([^\]]+)\]/g, function (m, innerMatch) {
    result = innerMatch;
  });
  return result;
};

const buildFilterModel = (values, operator, seperator) => {
  let prepareObject = baseFilterFieldModel();
  if (!operator || operator === 'range') {
    prepareObject.operator = 'null';
    if (Number.isInteger(Math.round(values[0])) || Number.isInteger(Math.round(values[1]))) {
      prepareObject.minValue = Number.isInteger(Math.round(values[0])) ? Math.round(values[0]) : null;
      prepareObject.maxValue = Number.isInteger(Math.round(values[1])) ? Math.round(values[1]) : null;
      prepareObject.includeNulls = values[2] || null;
      prepareObject.fieldType = 'int';
    } else {
      prepareObject.startDate = values[0];
      prepareObject.endDate = values[1];
      prepareObject.fieldType = 'date';
    }
  } else {
    prepareObject = _prepareObject(prepareObject, operator, values);
  }
  prepareObject['seperator'] = seperator;
  return prepareObject;
};

// Prepare object when operator is not range.
const _prepareObject = (prepareObject, operator, values) => {
  prepareObject.operator = operator.toLowerCase() === 'like' ? 'like (?)' : operator.toLowerCase();
  prepareObject.values = values;
  // adding extra check to check for valid date
  // as 'test 1' is also parsed as a valid date
  prepareObject.fieldType = moment(values[0], moment.ISO_8601).isValid() ? 'date' : 'nvarchar';
  return prepareObject;
};

export const businessRuleRefsFromWidget = (widgetDetail) => {
  let businessRuleRefs = [];
  if (widgetDetail.type === 'Process Flow') {
    let settings;
    try {
      settings = JSON.parse(widgetDetail.SettingsV2);
      businessRuleRefs = settings.goData.nodeDataArray
        .filter((node) => node.BusinessRuleRef)
        .map((node) => node.BusinessRuleRef);
    } catch (exceptions) {
      businessRuleRefs = [];
    }
  } else if (widgetDetail.BusinessRuleRef) {
    businessRuleRefs = [widgetDetail.BusinessRuleRef];
  }
  return businessRuleRefs;
};
